"use client";

import { LoadingOutlined } from "@ant-design/icons";
import classNames from "classnames";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SimpleBar from "simplebar-react";
import ArrowDownIcon from "./assets/arrow-down.svg";
import SelectedIcon from "./assets/selected-icon.svg";
import styles from "./index.module.css";

export type EditorSelectValue = string;

export interface EditorSelectOption {
  /** 选项值 */
  value: EditorSelectValue;
  /** 选项显示文本 */
  label: string;
  /** 选项描述文本 */
  description?: string;
  /** 选项图标 */
  icon?: ReactNode;
}

export interface EditorSelectCustomItem {
  /** 自定义项唯一标识 */
  key: string;
  /** 自定义项渲染内容 */
  render: () => ReactNode;
  /** 点击事件（默认不会关闭下拉框，除非提供了 value） */
  onClick?: () => void;
  /** 关联的值，如果匹配当前 value 则显示 label 并在点击时关闭 */
  value?: EditorSelectValue;
  /** 关联的显示文本 */
  label?: string;
}

export interface EditorSelectRenderOptionContext {
  /** 选项数据 */
  option: EditorSelectOption;
  /** 是否选中 */
  isSelected: boolean;
  /** 默认渲染内容（包含图标、文本、选中标记） */
  defaultRender: ReactNode;
}

export interface EditorSelectProps {
  /** 选择器内容 */
  children?: ReactNode;
  /** 选项列表 */
  options?: EditorSelectOption[];
  /** 当前选中值 */
  value?: EditorSelectValue;
  /** 选择变化回调 */
  onChange?: (value: EditorSelectValue) => void;
  /** 自定义类名 */
  className?: string;
  /** 点击事件 */
  onClick?: () => void;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否加载中 */
  loading?: boolean;
  /** 是否打开状态 */
  open?: boolean;
  /** 按钮图标 */
  icon?: ReactNode;
  /** 自定义选项渲染（可访问默认渲染内容） */
  renderOption?: (context: EditorSelectRenderOptionContext) => ReactNode;
  /** 自定义项列表（在选项列表之后） */
  customItems?: EditorSelectCustomItem[];
  /** 下拉框宽度（像素值），如不指定则与按钮宽度相同 */
  dropdownWidth?: number;
  /** 按钮高度（像素值），默认 40px */
  buttonHeight?: number;
  /** 打开时是否滚动至选中项 */
  scrollToSelectedOnOpen?: boolean;
}

export function EditorSelect({
  children,
  options = [],
  value,
  onChange,
  className,
  onClick,
  disabled = false,
  loading = false,
  open = false,
  icon,
  renderOption,
  customItems = [],
  dropdownWidth,
  buttonHeight,
  scrollToSelectedOnOpen = true,
}: EditorSelectProps) {
  const [isOpen, setIsOpen] = useState(open);
  const [isExiting, setIsExiting] = useState(false);
  const [shouldRender, setShouldRender] = useState(open);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLDivElement | null>(null);
  const exitTimerRef = useRef<NodeJS.Timeout | null>(null);
  const setSelectedOptionNode = useCallback((node: HTMLDivElement | null) => {
    selectedOptionRef.current = node;
  }, []);

  const selectedOption = options.find((option) => option.value === value);
  const selectedCustomItem = customItems.find((item) => item.value === value);

  const openDropdown = useCallback(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    setIsExiting(false);
    setShouldRender(true);
    setIsOpen(true);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsExiting(true);
    setIsOpen(false);
    // 等待退出动画完成后再卸载
    exitTimerRef.current = setTimeout(() => {
      setShouldRender(false);
      setIsExiting(false);
    }, 150); // 与 CSS fadeOut 动画时长一致
  }, []);

  const handleClick = useCallback(() => {
    if (!disabled && !loading) {
      if (isOpen) {
        closeDropdown();
      } else {
        openDropdown();
      }
      onClick?.();
    }
  }, [disabled, loading, isOpen, closeDropdown, openDropdown, onClick]);

  const handleOptionClick = useCallback(
    (optionValue: EditorSelectValue) => {
      onChange?.(optionValue);
      closeDropdown();
    },
    [onChange, closeDropdown]
  );

  // 计算下拉框位置
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top - 4, // 在按钮上方 4px
        left: rect.left,
        width: dropdownWidth ?? rect.width,
      });
    }
  }, [isOpen, dropdownWidth]);

  useEffect(() => {
    if (!scrollToSelectedOnOpen || !isOpen || !selectedOptionRef.current) {
      return;
    }

    selectedOptionRef.current.scrollIntoView({ block: "center" });
  }, [scrollToSelectedOnOpen, isOpen, value]);

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // 检查是否点击在按钮或下拉框内部（包括滚动条）
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, closeDropdown]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      <div
        ref={containerRef}
        className={classNames(styles.selectContainer, className)}
      >
        <button
          ref={buttonRef}
          className={classNames(styles.editorSelect, {
            [styles.open]: isOpen,
            [styles.disabled]: disabled || loading,
            [styles.loading]: loading,
          })}
          style={buttonHeight ? { height: `${buttonHeight}px` } : undefined}
          onClick={handleClick}
          disabled={disabled || loading}
        >
          {icon && !loading && <div>{icon}</div>}
          <span className={styles.content}>
            {loading ? (
              <span className={styles.loadingText}>
                <LoadingOutlined className={styles.loadingIcon} />
                Loading...
              </span>
            ) : selectedOption ? (
              selectedOption.label
            ) : selectedCustomItem ? (
              selectedCustomItem.label
            ) : (
              children
            )}
          </span>
          {!loading && (
            <ArrowDownIcon
              className={classNames(styles.arrow, {
                [styles.arrowRotated]: isOpen,
              })}
            />
          )}
        </button>
      </div>

      {/* 选择列表 - 使用 Portal 挂载到 body */}
      {shouldRender &&
        (options.length > 0 || customItems.length > 0) &&
        createPortal(
          <div
            ref={dropdownRef}
            className={classNames(styles.dropdown, {
              [styles.dropdownExit]: isExiting,
            })}
            style={{
              position: "fixed",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              zIndex: 900,
            }}
          >
            <SimpleBar
              className={styles.dropdownContent}
              style={{ maxHeight: "240px" }}
              autoHide={false}
            >
              {customItems.map((item) => (
                <div
                  key={item.key}
                  className={styles.customItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    item.onClick?.();
                    if (item.value !== undefined) {
                      closeDropdown();
                    }
                  }}
                >
                  {item.render()}
                </div>
              ))}
              {options.map((option) => {
                const isSelected = option.value === value;
                const hasDescription = Boolean(option.description);
                const defaultRender = (
                  <>
                    <div className={styles.optionContent}>
                      {option.icon && (
                        <div className={styles.optionIcon}>{option.icon}</div>
                      )}
                      <div
                        className={classNames(styles.optionText, {
                          [styles.optionTextWithDescription]: hasDescription,
                        })}
                      >
                        <span
                          className={classNames(styles.optionLabel, {
                            [styles.optionLabelWithDescription]: hasDescription,
                          })}
                        >
                          {option.label}
                        </span>
                        {hasDescription && (
                          <span className={styles.optionDescription}>
                            {option.description}
                          </span>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <SelectedIcon className="w-4 h-4 shrink-0 text-graphite" />
                    )}
                  </>
                );

                return (
                  <div
                    key={option.value}
                    className={classNames(styles.option, {
                      [styles.optionSelected]: isSelected,
                    })}
                    ref={
                      scrollToSelectedOnOpen && isSelected
                        ? setSelectedOptionNode
                        : undefined
                    }
                    onClick={() => handleOptionClick(option.value)}
                  >
                    {renderOption
                      ? renderOption({ option, isSelected, defaultRender })
                      : defaultRender}
                  </div>
                );
              })}
            </SimpleBar>
          </div>,
          document.body
        )}
    </>
  );
}
